/**
 * AI Chat API Contracts (legacy)
 *
 * AI conversation endpoints (`/v1/ai/chat`, `/v1/ai-conversations`). Re-exported
 * from the root barrel; the parent chat pilot contracts beside it are subpath-only.
 */

import { z } from 'zod'
import { PaginationMetaSchema, PaginationParamsSchema } from '../common'
import {
  AiConversationSchema,
  AiMessageSchema,
  ConversationStatusSchema,
  MessageRoleSchema
} from '../../schemas/entities/ai-chat'

// Re-export for convenience
export { AiConversationSchema, AiMessageSchema, ConversationStatusSchema, MessageRoleSchema }
export type { AiConversation, AiMessage, ConversationStatus, MessageRole } from '../../schemas/entities/ai-chat'

// ============================================================================
// Chat Endpoint Schemas (POST /v1/ai/chat)
// ============================================================================

/**
 * POST /v1/ai/chat - Request
 */
export const ChatRequestSchema = z.object({
  message: z.string().min(1),
  conversationId: z.string().optional()
})

export type ChatRequest = z.infer<typeof ChatRequestSchema>

/**
 * POST /v1/ai/chat - Response
 */
export const ChatResponseSchema = z.object({
  content: z.string(),
  conversationId: z.string(),
  usage: z.object({
    inputTokens: z.number(),
    outputTokens: z.number()
  }),
  toolsUsed: z.array(z.string()).optional()
})

export type ChatResponse = z.infer<typeof ChatResponseSchema>

// ============================================================================
// Chat Stream Endpoint Schemas (POST /v1/ai/chat-stream)
// ============================================================================

/**
 * Message part schema (Vercel AI SDK format)
 */
export const MessagePartSchema = z.object({
  type: z.string(),
  text: z.string().optional()
})

export type MessagePart = z.infer<typeof MessagePartSchema>

/**
 * UI Message schema (Vercel AI SDK format)
 */
export const UIMessageSchema = z.object({
  id: z.string(),
  role: z.enum(['user', 'assistant', 'system']),
  parts: z.array(MessagePartSchema)
})

export type UIMessage = z.infer<typeof UIMessageSchema>

/**
 * POST /v1/ai/chat-stream - Request (Vercel AI SDK format)
 */
export const ChatStreamRequestSchema = z.object({
  messages: z.array(UIMessageSchema).min(1),
  conversationId: z.string().optional(),
  id: z.string().optional(),
  trigger: z.string().optional()
})

export type ChatStreamRequest = z.infer<typeof ChatStreamRequestSchema>

// ============================================================================
// Conversation List Schemas (for ai/v1/conversations)
// ============================================================================

/**
 * Conversation status enum for list/query
 */
export const ConversationStatusFilterSchema = z.enum(['ACTIVE', 'COMPLETED', 'ABANDONED', 'ARCHIVED', 'ERROR'])

export type ConversationStatusFilter = z.infer<typeof ConversationStatusFilterSchema>

/**
 * GET /v1/ai/conversations - Request
 */
export const ListConversationsRequestSchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).optional(),
  offset: z.coerce.number().int().min(0).optional(),
  status: ConversationStatusFilterSchema.optional()
})

export type ListConversationsRequest = z.infer<typeof ListConversationsRequestSchema>

/**
 * Conversation item in list response
 */
export const ConversationListItemSchema = z.object({
  id: z.string(),
  status: z.string(),
  startedAt: z.string(),
  lastActivityAt: z.string(),
  totalMessages: z.number(),
  firstMessageContent: z.string().nullable()
})

export type ConversationListItem = z.infer<typeof ConversationListItemSchema>

/**
 * GET /v1/ai/conversations - Response
 */
export const ListConversationsResponseSchema = z.object({
  conversations: z.array(ConversationListItemSchema),
  total: z.number(),
  limit: z.number(),
  offset: z.number()
})

export type ListConversationsResponse = z.infer<typeof ListConversationsResponseSchema>

/**
 * GET /v1/ai/conversations/:id - Request
 */
export const GetSingleConversationRequestSchema = z.object({
  id: z.string().min(1)
})

export type GetSingleConversationRequest = z.infer<typeof GetSingleConversationRequestSchema>

/**
 * Message in single conversation response
 */
export const ConversationMessageSchema = z.object({
  id: z.string(),
  role: z.string(),
  content: z.string(),
  sequenceNumber: z.number(),
  createdAt: z.string()
})

export type ConversationMessage = z.infer<typeof ConversationMessageSchema>

/**
 * GET /v1/ai/conversations/:id - Response
 */
export const GetSingleConversationResponseSchema = z.object({
  id: z.string(),
  status: z.string(),
  startedAt: z.string(),
  lastActivityAt: z.string(),
  totalMessages: z.number(),
  messages: z.array(ConversationMessageSchema)
})

export type GetSingleConversationResponse = z.infer<typeof GetSingleConversationResponseSchema>

/**
 * DELETE /v1/ai/conversations/:id - Request
 */
export const DeleteConversationRequestSchema = z.object({
  id: z.string().min(1)
})

export type DeleteConversationRequest = z.infer<typeof DeleteConversationRequestSchema>

// ============================================================================
// AI Conversations Endpoint Schemas (for ai-conversations/v1)
// ============================================================================

/**
 * GET /v1/ai-conversations - Query
 *
 * NOTE: `school_user_id` is legacy/redundant — the backend resolves the calling
 * user from the JWT (`sub` → UsersOnSchools via platformId). The field remains
 * optional in the schema for backward compatibility with older clients (web
 * dashboard) that still send it; the backend ignores its value.
 */
export const GetAiConversationsQuerySchema = PaginationParamsSchema.extend({
  school_user_id: z.string().min(1).optional(),
  status: ConversationStatusFilterSchema.optional(),
  startedAfter: z.string().optional(),
  startedBefore: z.string().optional(),
  allUsers: z.boolean().optional()
})

export type GetAiConversationsQuery = z.infer<typeof GetAiConversationsQuerySchema>

/**
 * Conversation item in ai-conversations list
 */
export const AiConversationItemSchema = z.object({
  id: z.string(),
  status: z.string(),
  title: z.string().nullable(),
  preview: z.string().nullable(),
  totalMessages: z.number(),
  startedAt: z.string(),
  endedAt: z.string().nullable(),
  lastActivityAt: z.string()
})

export type AiConversationItem = z.infer<typeof AiConversationItemSchema>

/**
 * GET /v1/ai-conversations - Response (paginated list)
 */
export const GetAiConversationsResponseSchema = z.object({
  data: z.array(AiConversationItemSchema),
  meta: z.object({
    total: z.number(),
    limit: z.number(),
    offset: z.number()
  }),
  message: z.string()
})

export type GetAiConversationsResponse = z.infer<typeof GetAiConversationsResponseSchema>

/**
 * Message item in single conversation
 */
export const AiConversationMessageItemSchema = z.object({
  id: z.string(),
  role: z.enum(['USER', 'ASSISTANT', 'SYSTEM']),
  content: z.string(),
  sequenceNumber: z.number(),
  audioDurationSeconds: z.number().nullable(),
  createdAt: z.string()
})

export type AiConversationMessageItem = z.infer<typeof AiConversationMessageItemSchema>

/**
 * GET /v1/ai-conversations/:conversationId - Response (single conversation with messages)
 */
export const GetAiConversationResponseSchema = z.object({
  id: z.string(),
  status: z.string(),
  totalMessages: z.number(),
  startedAt: z.string(),
  endedAt: z.string().nullable(),
  title: z.string().nullable(),
  messages: z.array(AiConversationMessageItemSchema)
})

export type GetAiConversationResponse = z.infer<typeof GetAiConversationResponseSchema>

/**
 * PATCH /v1/ai-conversations/:conversationId - Request
 */
export const UpdateAiConversationRequestSchema = z.object({
  title: z.string().max(200).optional()
})

export type UpdateAiConversationRequest = z.infer<typeof UpdateAiConversationRequestSchema>

/**
 * Common response for update/archive/restore operations
 */
export const AiConversationOperationResponseSchema = GetAiConversationResponseSchema.omit({ messages: true })

export type AiConversationOperationResponse = z.infer<typeof AiConversationOperationResponseSchema>

// ============================================================================
// Legacy Aliases (for backwards compatibility)
// ============================================================================

/**
 * GET /v1/ai-conversations
 * @deprecated Use GetAiConversationsQuerySchema and GetAiConversationsResponseSchema
 */
export const GetConversationsRequestSchema = PaginationParamsSchema.extend({
  status: z.enum(['ACTIVE', 'ARCHIVED']).optional()
})

export type GetConversationsRequest = z.infer<typeof GetConversationsRequestSchema>

export const GetConversationsResponseSchema = z.object({
  data: z.array(AiConversationSchema),
  meta: PaginationMetaSchema.optional(),
  message: z.string()
})

export type GetConversationsResponse = z.infer<typeof GetConversationsResponseSchema>

/**
 * GET /v1/ai-conversations/:conversationId
 * @deprecated Use GetAiConversationResponseSchema
 */
export const GetConversationResponseSchema = z.object({
  data: AiConversationSchema.extend({
    messages: z.array(AiMessageSchema)
  }),
  message: z.string()
})

export type GetConversationResponse = z.infer<typeof GetConversationResponseSchema>

/**
 * PATCH /v1/ai-conversations/:conversationId
 * @deprecated Use UpdateAiConversationRequestSchema
 */
export const UpdateConversationRequestSchema = z.object({
  title: z.string().optional()
})

export type UpdateConversationRequest = z.infer<typeof UpdateConversationRequestSchema>

export const UpdateConversationResponseSchema = z.object({
  data: AiConversationSchema,
  message: z.string()
})

export type UpdateConversationResponse = z.infer<typeof UpdateConversationResponseSchema>

/**
 * PATCH /v1/ai-conversations/:conversationId/archive
 */
export const ArchiveConversationResponseSchema = z.object({
  data: AiConversationSchema,
  message: z.string()
})

export type ArchiveConversationResponse = z.infer<typeof ArchiveConversationResponseSchema>

/**
 * PATCH /v1/ai-conversations/:conversationId/restore
 */
export const RestoreConversationResponseSchema = z.object({
  data: AiConversationSchema,
  message: z.string()
})

export type RestoreConversationResponse = z.infer<typeof RestoreConversationResponseSchema>
